import cx from 'classnames'

import { workspacesSet } from 'redux/actions'
import { collectResults } from 'utils/query'

import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { useDispatch } from 'react-redux'
import { useTransformedBrandPlanSchedules } from 'screens/BrandKits/components/BrandPlanTimeline/useTransformedBrandPlanSchedules'

import { ConfirmPauseModal } from './ConfirmPauseModal/ConfirmPauseModal'
import { ConfirmResumeModal } from './ConfirmResumeModal/ConfirmResumeModal'
import SectionStyles from 'components/Settings/Section.module.scss'
import Styles from './AutopilotSettings.module.scss'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import dayjs from 'dayjs'
import { useModals } from 'almanac-ui/Modal'
import { TransformedBrandPlanSchedule } from 'screens/BrandKits/components/BrandPlanTimeline/Types'
import { ConfirmApprovalsModal } from './ConfirmApprovalsModal/ConfirmApprovalsModal'
import { useQueryClient } from 'react-query'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'

export const AutopilotSettings = observer(({ brandPlanId }: { brandPlanId: number }) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const workspace = useCurrentWorkspace()
  const workspaces = useWorkspaces()
  const { currentSubscription } = useRootStore().tierUsageStore
  const isPostApprovalEnabled =
    useFlipperFlag('post_approvals') && currentSubscription?.plan === PaymentPlanKey.BlazeAutopilotGrowth
  const { openModal, closeModal } = useModals()
  const schedulesQuery = useTransformedBrandPlanSchedules({ brandPlanId })
  const schedules = collectResults(schedulesQuery, (data) => data.schedules)
  const currentSchedule = schedules.find((s: TransformedBrandPlanSchedule) => s.is_current)
  const currentScheduleIndex = currentSchedule ? schedules.indexOf(currentSchedule) : null
  const nextSchedule =
    currentScheduleIndex !== null && currentScheduleIndex !== -1 ? schedules[currentScheduleIndex - 1] : null

  if (!workspace?.is_virtual_marketer) return null

  const toggleAutopilot = async (
    newPauseAutopilotGenerateAt: string | null | undefined,
    newPauseAutopilotPublishAt: string | null | undefined,
    newResumeAutopilotGenerateAt: string | null | undefined,
    newResumeAutopilotPublishAt: string | null | undefined,
  ) => {
    if (!workspace) return

    const prevPauseAutopilotGenerateAt = workspace.pause_autopilot_generate_at
    const prevPauseAutopilotPublishAt = workspace.pause_autopilot_publish_at
    const prevResumeAutopilotGenerateAt = workspace.resume_autopilot_generate_at
    const prevResumeAutopilotPublishAt = workspace.resume_autopilot_publish_at

    dispatch(
      workspacesSet(
        workspaces.map((ws) =>
          ws.id === workspace.id
            ? {
                ...ws,
                pause_autopilot_generate_at: newPauseAutopilotGenerateAt,
                pause_autopilot_publish_at: newPauseAutopilotPublishAt,
                resume_autopilot_generate_at: newResumeAutopilotGenerateAt,
                resume_autopilot_publish_at: newResumeAutopilotPublishAt,
              }
            : ws,
        ),
      ),
    )

    const res = await sendToApi<void>(`/w/${workspace.id}/update`, {
      method: 'PUT',
      body: {
        pause_autopilot_generate_at: newPauseAutopilotGenerateAt,
        pause_autopilot_publish_at: newPauseAutopilotPublishAt,
        resume_autopilot_generate_at: newResumeAutopilotGenerateAt,
        resume_autopilot_publish_at: newResumeAutopilotPublishAt,
      },
    })

    if (res.type !== SUCCESS_RESPONSE_TYPE) {
      showToastMessage("We couldn't save your settings.", 'error')
      dispatch(
        workspacesSet(
          workspaces.map((ws) =>
            ws.id === workspace.id
              ? {
                  ...ws,
                  pause_autopilot_generate_at: prevPauseAutopilotGenerateAt,
                  pause_autopilot_publish_at: prevPauseAutopilotPublishAt,
                  resume_autopilot_generate_at: prevResumeAutopilotGenerateAt,
                  resume_autopilot_publish_at: prevResumeAutopilotPublishAt,
                }
              : ws,
          ),
        ),
      )
    }
  }

  const toggleAllAutopilot = () => {
    const current = !!workspace.pause_autopilot_generate_at && !!workspace.pause_autopilot_publish_at
    const value = current ? null : dayjs().toISOString()

    if (value) {
      openModal(
        ConfirmPauseModal,
        {
          variant: 'all',
          onConfirm: () => {
            toggleAutopilot(value, value, null, null)
            closeModal()
          },
        },
        {
          newStack: true,
        },
      )
      return
    }

    if (nextSchedule?.startDate) {
      openModal(
        ConfirmResumeModal,
        {
          variant: 'all',
          onConfirm: (resumeAt) => {
            const resumeAtValue = resumeAt === 'next-week' ? nextSchedule?.startDate.toISOString() : null
            toggleAutopilot(value, value, resumeAtValue, resumeAtValue)
            closeModal()
          },
          startDate: nextSchedule.startDate,
        },
        {
          newStack: true,
        },
      )
    } else {
      // No next schedule available, resume immediately
      toggleAutopilot(value, value, null, null)
    }
  }

  const togglePauseAutopilotGenerate = () => {
    const pauseAt = workspace.pause_autopilot_generate_at ? null : dayjs().toISOString()

    if (pauseAt) {
      openModal(
        ConfirmPauseModal,
        {
          startDate: nextSchedule?.startDate,
          variant: 'generate',
          onConfirm: () => {
            toggleAutopilot(pauseAt, workspace.pause_autopilot_publish_at, null, workspace.resume_autopilot_publish_at)
            closeModal()
          },
        },
        {
          newStack: true,
        },
      )
      return
    }

    if (nextSchedule?.startDate) {
      openModal(
        ConfirmResumeModal,
        {
          variant: 'generate',
          onConfirm: (resumeAt) => {
            const resumeGenerateAtValue = resumeAt === 'next-week' ? nextSchedule?.startDate.toISOString() : null
            toggleAutopilot(
              pauseAt,
              workspace.pause_autopilot_publish_at,
              resumeGenerateAtValue,
              workspace.resume_autopilot_publish_at,
            )
            closeModal()
          },
          startDate: nextSchedule.startDate,
        },
        {
          newStack: true,
        },
      )
    } else {
      // No next schedule available, resume immediately
      toggleAutopilot(pauseAt, workspace.pause_autopilot_publish_at, null, workspace.resume_autopilot_publish_at)
    }
  }

  const togglePauseAutopilotPublish = () => {
    const pauseAt = workspace.pause_autopilot_publish_at ? null : dayjs().toISOString()

    if (pauseAt) {
      openModal(
        ConfirmPauseModal,
        {
          variant: 'publish',
          onConfirm: () => {
            toggleAutopilot(
              workspace.pause_autopilot_generate_at,
              pauseAt,
              workspace.resume_autopilot_generate_at,
              null,
            )
            queryClient.invalidateQueries(filesKeys.all(workspace.id))
            closeModal()
          },
        },
        {
          newStack: true,
        },
      )
      return
    }

    if (nextSchedule?.startDate) {
      openModal(
        ConfirmResumeModal,
        {
          variant: 'publish',
          onConfirm: (resumeAt) => {
            const resumePublishAtValue = resumeAt === 'next-week' ? nextSchedule.startDate.toISOString() : null
            toggleAutopilot(
              workspace.pause_autopilot_generate_at,
              pauseAt,
              workspace.resume_autopilot_generate_at,
              resumePublishAtValue,
            )
            queryClient.invalidateQueries(filesKeys.all(workspace.id))
            closeModal()
          },
          startDate: nextSchedule.startDate,
        },
        {
          newStack: true,
        },
      )
    } else {
      // No next schedule available, resume immediately
      toggleAutopilot(workspace.pause_autopilot_generate_at, pauseAt, workspace.resume_autopilot_generate_at, null)
      queryClient.invalidateQueries(filesKeys.all(workspace.id))
    }
  }

  const togglePostApproval = () => {
    const isPostApprovalEnabled = !!workspace.post_approval_enabled

    const toggleApprovals = async () => {
      dispatch(
        workspacesSet(
          workspaces.map((ws) =>
            ws.id === workspace.id
              ? {
                  ...ws,
                  post_approval_enabled: !isPostApprovalEnabled,
                }
              : ws,
          ),
        ),
      )

      const res = await sendToApi<void>(`/w/${workspace.id}/update`, {
        method: 'PUT',
        body: {
          post_approval_enabled: !isPostApprovalEnabled,
        },
      })

      if (res.type !== SUCCESS_RESPONSE_TYPE) {
        showToastMessage("We couldn't save your settings.", 'error')
        dispatch(
          workspacesSet(
            workspaces.map((ws) =>
              ws.id === workspace.id
                ? {
                    ...ws,
                    post_approval_enabled: workspace.post_approval_enabled,
                  }
                : ws,
            ),
          ),
        )
      }

      queryClient.invalidateQueries(filesKeys.all(workspace.id))
    }

    openModal(
      ConfirmApprovalsModal,
      {
        enabled: isPostApprovalEnabled,
        onConfirm: () => {
          toggleApprovals()
          closeModal()
        },
      },
      { newStack: true },
    )
  }

  return (
    <div className={SectionStyles.sectionRow}>
      <div className={cx(SectionStyles.sectionFieldWrapper, SectionStyles.alignTop, SectionStyles.noPadding)}>
        <div className={SectionStyles.rowStyles}>
          <Heading level={5}>Enable Autopilot</Heading>
          <ToggleButton
            checked={!workspace.pause_autopilot_generate_at || !workspace.pause_autopilot_publish_at}
            name="enable-autopilot"
            handleChange={toggleAllAutopilot}
          />
        </div>
        <div className={cx(SectionStyles.sectionInput, SectionStyles.editing)}>
          <p className={SectionStyles.text}>
            Automatically generate and publish your content. Turn off to pause both actions.
          </p>
        </div>
        <div className={Styles.togglesContainer}>
          <div className={Styles.toggleContainer}>
            <Text variant="smallList">Auto Content Generation</Text>
            <ToggleButton
              checked={!workspace.pause_autopilot_generate_at}
              name="auto-content-generation"
              handleChange={togglePauseAutopilotGenerate}
              size="sm-md"
            />
          </div>
          <div className={Styles.toggleContainer}>
            <Text variant="smallList">Auto Publishing</Text>
            <ToggleButton
              checked={!workspace.pause_autopilot_publish_at}
              name="auto-publishing"
              handleChange={togglePauseAutopilotPublish}
              size="sm-md"
            />
          </div>
          {isPostApprovalEnabled && (
            <div className={Styles.toggleContainer}>
              <Text variant="smallList">Approve content before auto-posting</Text>
              <ToggleButton
                checked={!!workspace.post_approval_enabled}
                name="post-approval"
                handleChange={togglePostApproval}
                size="sm-md"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
})
